import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { FaCalendarTimes, FaTrash, FaPlus, FaUser, FaFilter, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

export default function Unavailability() {
  const { userDetails } = useAuth();
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [servicePeriod, setServicePeriod] = useState('Ambos'); // NOVO: Manhã, Noite, Ambos

  // Estados de Admin
  const [musicians, setMusicians] = useState([]);
  const [targetMusicianId, setTargetMusicianId] = useState('');
  const [filterMusicianId, setFilterMusicianId] = useState('');

  // Filtro de Mês (Admin) - Inicializa com o mês atual no formato 'YYYY-MM'
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );

  const [loading, setLoading] = useState(true);

  // === NOVO: ESTADO DE FEEDBACK ===
  const [feedback, setFeedback] = useState(null);

  const unavailRef = collection(db, "unavailability");
  const musiciansRef = collection(db, "musicians");

  // === FUNÇÃO DE FEEDBACK VISUAL ===
  const showFeedback = (msg, type = 'success') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  // === CARREGAR DADOS ===
  const loadData = async () => {
    if (!userDetails) return;
    setLoading(true);
    try {
      let list = [];

      if (userDetails.isAdmin) {
        // 1. Carregar Músicos
        const musQuery = query(musiciansRef, where("isActive", "==", true));
        const musSnap = await getDocs(musQuery);

        const musList = musSnap.docs.map(d => ({ ...d.data(), id: d.id }));
        musList.sort((a, b) => a.name.localeCompare(b.name));
        setMusicians(musList);

        // 2. Carregar Indisponibilidades
        let q = unavailRef;
        if (filterMusicianId) {
          q = query(unavailRef, where("musicianId", "==", filterMusicianId));
        }

        const snap = await getDocs(q);
        list = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      } else {
        // Membro
        const q = query(unavailRef, where("musicianId", "==", userDetails.musicianId));
        const snap = await getDocs(q);
        list = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      }

      list.sort((a, b) => new Date(a.date) - new Date(b.date));
      setDates(list);

    } catch (error) {
      console.error("Erro ao carregar:", error);
      showFeedback("Erro de conexão ao carregar dados.", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, filterMusicianId]);

  // === ADICIONAR ===
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDate) return;

    // 1. Definir QUEM é o dono
    let finalMusicianId = userDetails.musicianId;
    let finalMusicianName = userDetails.musicianName;

    if (userDetails.isAdmin && targetMusicianId) {
      const selected = musicians.find(m => m.id === targetMusicianId);
      if (selected) {
        finalMusicianId = selected.id;
        finalMusicianName = selected.name;
      }
    }

    // 2. Validação de Data
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = newDate.split('-');
    const selectedDate = new Date(y, m - 1, d, 12, 0, 0);

    if (selectedDate < today) {
      showFeedback("Você não pode marcar indisponibilidade no passado.", "error");
      return;
    }

    // 3. Validação de Duplicidade (Banco)
    try {
      const qDuplicate = query(
        unavailRef,
        where("musicianId", "==", finalMusicianId),
        where("date", "==", newDate)
      );
      const snapshot = await getDocs(qDuplicate);

      if (!snapshot.empty) {
        showFeedback(`Já existe uma ausência marcada para ${finalMusicianName} nesta data.`, "error");
        return;
      }

      // Salvar
      await addDoc(unavailRef, {
        musicianId: finalMusicianId,
        musicianName: finalMusicianName,
        date: newDate,
        servicePeriod: (selectedDate.getDay() === 0) ? servicePeriod : 'Ambos', // NOVO: Salva período se domingo
        createdAt: new Date()
      });

      showFeedback(`Ausência registrada para ${finalMusicianName}.`);
      setNewDate('');
      loadData();

    } catch (error) {
      console.error(error);
      showFeedback("Erro ao salvar indisponibilidade.", "error");
    }
  };

  const handleDelete = async (item) => {
    if (window.confirm(`Remover ausência de ${item.musicianName} no dia ${formatDate(item.date)}?`)) {
      try {
        await deleteDoc(doc(db, "unavailability", item.id));
        showFeedback("Disponibilidade restaurada.");
        loadData();
      } catch (error) {
        console.error("Erro ao deletar:", error);
        showFeedback("Erro ao excluir.", "error");
      }
    }
  };

  const formatDate = (dateString, period) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const dateObj = new Date(year, month - 1, day);
    let formatted = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });

    // Se for domingo e o período não for 'Ambos', exibir o período
    if (dateObj.getDay() === 0 && period && period !== 'Ambos') {
      formatted += ` (${period})`;
    }
    return formatted;
  };

  return (
    <div>
      {/* === BANNER DE FEEDBACK (Igual às outras telas) === */}
      {feedback && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-down ${feedback.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {feedback.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span className="font-bold">{feedback.msg}</span>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Gestão de Indisponibilidades</h1>

      {/* === CARD DE CADASTRO === */}
      <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-red-400 mb-8 animate-fade-in">
        <h3 className="text-lg font-bold text-gray-700 mb-2">
          {userDetails?.isAdmin ? 'Registrar Ausência' : 'Avisar Minha Ausência'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Selecione a data que {userDetails?.isAdmin ? 'o músico' : 'você'} <strong>NÃO</strong> poderá tocar.
        </p>

        <form onSubmit={handleAdd} className="grid gap-4 md:grid-cols-3 items-end">

          {/* SELECT DE MÚSICO (SÓ ADMIN) */}
          {userDetails?.isAdmin && (
            <div className="w-full">
              <label className="block text-sm font-bold text-gray-600 mb-1">Quem faltará?</label>
              <select
                value={targetMusicianId}
                onChange={(e) => setTargetMusicianId(e.target.value)}
                className="w-full border p-2 rounded outline-none focus:border-red-400 bg-white"
              >
                <option value="">Eu mesmo ({userDetails.musicianName})</option>
                {musicians.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="w-full">
            <label className="block text-sm font-bold text-gray-600 mb-1">Data da Ausência</label>
            <input
              type="date"
              value={newDate}
              onChange={e => {
                setNewDate(e.target.value);
                // Reseta o período ao trocar a data
                setServicePeriod('Ambos');
              }}
              className="w-full border p-2 rounded outline-none focus:border-red-400"
              required
            />
          </div>

          {/* NOVO: SELECT DE PERÍODO (SÓ APARECE SE FOR DOMINGO) */}
          {newDate && (() => {
            const [y, m, d] = newDate.split('-');
            const dateObj = new Date(y, m - 1, d);
            return dateObj.getDay() === 0;
          })() && (
              <div className="w-full">
                <label className="block text-sm font-bold text-gray-600 mb-1">Qual Culto?</label>
                <select
                  value={servicePeriod}
                  onChange={(e) => setServicePeriod(e.target.value)}
                  className="w-full border p-2 rounded outline-none focus:border-red-400 bg-white"
                >
                  <option value="Ambos">Ambos (Dia Todo)</option>
                  <option value="Manhã">Somente Manhã</option>
                  <option value="Noite">Somente Noite</option>
                </select>
              </div>
            )}

          <button type="submit" className="bg-red-500 text-white font-bold px-6 py-2 rounded hover:bg-red-600 h-10 flex items-center justify-center gap-2 w-full md:w-auto">
            <FaPlus /> Adicionar
          </button>
        </form>
      </div>

      {/* === FILTROS DE VISUALIZAÇÃO (SÓ ADMIN) === */}
      {userDetails?.isAdmin && (
        <div className="flex flex-wrap items-center gap-4 mb-4 bg-gray-100 p-3 rounded">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-500" />
            <span className="text-sm font-bold text-gray-600">Mês:</span>
            <input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="bg-white border border-gray-300 rounded px-2 py-1 text-sm font-medium text-brand-blue cursor-pointer outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-600">Músico:</span>
            <select
              value={filterMusicianId}
              onChange={(e) => setFilterMusicianId(e.target.value)}
              className="bg-white border border-gray-300 rounded px-2 py-1 text-sm font-medium text-brand-blue cursor-pointer outline-none"
            >
              <option value="">Todos</option>
              {musicians.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* === LISTAGEM === */}
      {loading ? <div className="text-center p-10 text-gray-400">Carregando...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-20">
          {dates
            .filter(item => {
              // Filtro de mês (admin): compara 'YYYY-MM' da data com o filtro selecionado
              if (userDetails?.isAdmin && filterMonth) {
                return item.date && item.date.startsWith(filterMonth);
              }
              return true;
            })
            .map(item => (
              <div key={item.id} className="bg-red-50 p-4 rounded border border-red-200 flex justify-between items-center shadow-sm hover:bg-red-100 transition">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-full text-red-400 shadow-sm">
                    <FaCalendarTimes />
                  </div>
                  <div>
                    <span className="block font-bold text-gray-800 text-lg">{formatDate(item.date, item.servicePeriod)}</span>
                    <span className="text-xs text-red-600 flex items-center gap-1 font-semibold uppercase">
                      <FaUser size={10} /> {item.musicianName}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(item)}
                  className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-white transition"
                  title="Remover ausência"
                >
                  <FaTrash />
                </button>
              </div>
            ))}

          {dates.filter(item => {
            if (userDetails?.isAdmin && filterMonth) {
              return item.date && item.date.startsWith(filterMonth);
            }
            return true;
          }).length === 0 && (
              <div className="col-span-full text-center p-8 text-gray-400 italic bg-white rounded border border-dashed">
                {filterMusicianId || filterMonth ? "Nenhuma ausência encontrada para este filtro." : "Nenhuma ausência registrada no sistema."}
              </div>
            )}
        </div>
      )}
    </div>
  );
}